import { useState, useMemo, Fragment, type ReactNode } from 'react';
import classNames from 'classnames/bind';
import { AlertCircle, AlertTriangle, CheckCircle, ChevronDown } from '@globalvisioninc/assets/icons';
import { Button } from '@globalvisioninc/ui/button';
import { Select, SELECT_EMPTY_VALUE } from '@globalvisioninc/ui/select';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { DocumentTypes } from 'types';
import PDFManagerFactory from 'pdftron/PDFManagerFactory';
import store from 'store/store';
import { app, inspection, getFocusedMatchIndex } from 'store';
import { useZoomStateStore } from 'zstore/zoomStateStore';
import { useSelector } from 'react-redux';
import styles from './StepAndRepeatDropdown.module.css';

const cx = classNames.bind(styles);

interface Repeat {
  index: number;
  differences: unknown[];
  differencesPostFiltering?: unknown[];
}

interface StepAndRepeatDropdownProps {
  repeatsPerPage: Record<number, Repeat[]>;
  isStepAndRepeat: boolean;
}

const getDisplayValue = (pageNumber: string, repeatIndex: number): string =>
  `Source (P${pageNumber}) v. Repeat ${repeatIndex + 1}`;

const preventSelectDismiss = (event: { preventDefault: () => void }) => {
  // Keep the Select panel open while interacting with non-option controls
  // (page headers / expand toggles). Radix closes on pointer-down outside items.
  event.preventDefault();
};

const StepAndRepeatDropdown = ({ repeatsPerPage, isStepAndRepeat }: StepAndRepeatDropdownProps) => {
  const focusedMatchIndex = useSelector(getFocusedMatchIndex);
  const [isOpen, setIsOpen] = useState(false);

  const sourcePages = useMemo(() => Object.keys(repeatsPerPage || {}), [repeatsPerPage]);

  const displayValueToRepeatIndex = useMemo(() => {
    const map: Record<string, number> = {};
    for (const [pageNumber, repeats] of Object.entries(repeatsPerPage || {})) {
      for (const repeat of repeats) {
        map[getDisplayValue(pageNumber, repeat.index)] = repeat.index;
      }
    }
    return map;
  }, [repeatsPerPage]);

  const getDisplayValueForRepeatIndex = (repeatIndex: number): string | null => {
    for (const [pageNumber, repeats] of Object.entries(repeatsPerPage || {})) {
      const repeat = repeats.find((item) => item.index === repeatIndex);
      if (repeat) {
        return getDisplayValue(pageNumber, repeat.index);
      }
    }
    return null;
  };

  const selectedValue = useMemo(() => {
    if (focusedMatchIndex === -1) return null;
    return getDisplayValueForRepeatIndex(focusedMatchIndex);
  }, [focusedMatchIndex, repeatsPerPage]);

  const displayedValue = useMemo(() => {
    if (selectedValue) return selectedValue;
    if (sourcePages.length === 0) return '';
    const firstPageNumber = sourcePages[0];
    const firstPageRepeats = repeatsPerPage[Number(firstPageNumber)];
    if (firstPageRepeats?.length > 0) {
      return getDisplayValue(firstPageNumber, firstPageRepeats[0].index);
    }
    return '';
  }, [selectedValue, sourcePages, repeatsPerPage]);

  const [expandedPages, setExpandedPages] = useState<Record<string, boolean>>(() => {
    const allExpanded: Record<string, boolean> = {};
    for (const pageNumber of sourcePages) {
      allExpanded[pageNumber] = true;
    }
    return allExpanded;
  });

  if (!isStepAndRepeat) return null;

  const setPageExpanded = (pageNumber: string, isExpanded: boolean) => {
    setExpandedPages((previous) => ({
      ...previous,
      [pageNumber]: isExpanded,
    }));
  };

  const handleDeselectRepeat = () => {
    const docManager = PDFManagerFactory.getPDFDocManager();
    if (!docManager) return;

    const dispatch = store.dispatch;
    dispatch(inspection.actions.unfocusDifference());

    PDFAnnotationManager.getInstance(DocumentTypes.source)?.deselectAllAnnotations();
    PDFAnnotationManager.getInstance(DocumentTypes.target)?.deselectAllAnnotations();

    docManager.getInstance(DocumentTypes.source)?.removeHighContrasts();
    docManager.getInstance(DocumentTypes.target)?.removeHighContrasts();
    dispatch(app.actions.setFocusedMatchIndex(-1));

    const { zoomLockLevel, setZoomChanging } = useZoomStateStore.getState();
    const targetWrapper = docManager.getInstance(DocumentTypes.target);
    const sourceWrapper = docManager.getInstance(DocumentTypes.source);
    if (!zoomLockLevel[DocumentTypes.target] && targetWrapper) targetWrapper.fitToPage();
    if (!zoomLockLevel[DocumentTypes.source] && sourceWrapper) sourceWrapper.fitToPage();
    setZoomChanging();
  };

  const handleSelectRepeat = (displayValue: string) => {
    if (displayValue === SELECT_EMPTY_VALUE) return;
    const repeatIndex = displayValueToRepeatIndex[displayValue];
    if (repeatIndex === undefined) return;

    const repeatAnnotation = PDFAnnotationManager.getInstance(DocumentTypes.target)?.getAnnotationById(
      `repeat-${repeatIndex}`,
    );

    if (repeatAnnotation) {
      PDFAnnotationManager.repeatAnnotationSelected(repeatAnnotation);
    } else {
      store.dispatch(app.actions.setFocusedMatchIndex(repeatIndex));
    }
  };

  const renderStatusIcon = (numResults: number, isFilteredOut: boolean): ReactNode => {
    if (numResults === 0) {
      if (isFilteredOut) {
        return (
          <GVTooltip title="Clear filters to see results" side="right">
            <Button.Root mode="tertiary" className={cx(styles.icon, styles.muted)} title="Clear filters to see results">
              <AlertCircle />
            </Button.Root>
          </GVTooltip>
        );
      }
      return <CheckCircle className={cx(styles.icon, styles.pass)} />;
    }
    return <AlertTriangle className={cx(styles.icon, styles.warn)} />;
  };

  const pageEntries = Object.entries(repeatsPerPage);

  // Unset uses SELECT_EMPTY_VALUE so the floating label stays active without
  // marking a visible repeat as checked. Item hover/active/focus colors then
  // come only from Select panel item tokens.
  const selectValue = selectedValue ?? SELECT_EMPTY_VALUE;

  return (
    <Select.Root
      id="step-and-repeat-dropdown"
      label="Jump to"
      value={selectValue}
      onValueChange={handleSelectRepeat}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className={styles.select}
      renderValue={() => displayedValue}
    >
      <Select.Trigger />
      <Select.Portal>
        <Select.Content className={styles.panel} position="popper" sideOffset={4}>
          <Select.Viewport>
            <Select.Item value={SELECT_EMPTY_VALUE} className={styles.hidden}>
              {'\u200b'}
            </Select.Item>
            {pageEntries.map(([pageNumber, repeats], pageIndex) => {
              const isExpanded = expandedPages[pageNumber];
              const isLastPage = pageIndex === pageEntries.length - 1;

              return (
                <Fragment key={pageNumber}>
                  <Select.Group>
                    <div className={styles.header}>
                      <Button.Root
                        mode="tertiary"
                        className={cx(styles.expand, {
                          isExpanded,
                        })}
                        data-testid="step-repeat-expand"
                        title={isExpanded ? 'Collapse page' : 'Expand page'}
                        onPointerDown={preventSelectDismiss}
                        onClick={(event) => {
                          event.stopPropagation();
                          setPageExpanded(pageNumber, !isExpanded);
                        }}
                      >
                        <ChevronDown />
                      </Button.Root>
                      <Button.Root
                        mode="tertiary"
                        className={styles.headerButton}
                        data-testid={`repeat-header-${pageNumber}`}
                        label={`Source (P${pageNumber})`}
                        elementEnd={
                          <span className={styles.repeatsText}>
                            {`${repeats.length} repeat${repeats.length === 1 ? '' : 's'}`}
                          </span>
                        }
                        onPointerDown={preventSelectDismiss}
                        onClick={handleDeselectRepeat}
                      />
                    </div>
                    {isExpanded &&
                      repeats.map((repeat, index) => {
                        const numResults = repeat.differencesPostFiltering?.length || 0;
                        const originalResults = repeat.differences?.length || 0;
                        const isFilteredOut = originalResults > 0 && numResults === 0;
                        const displayValue = getDisplayValue(pageNumber, repeat.index);

                        return (
                          <Select.Item
                            key={`repeat-${pageNumber}-${repeat.index || index}`}
                            value={displayValue}
                            data-testid={`repeat-menuitem-${pageNumber}-${repeat.index || index}`}
                            className={cx(styles.item, {
                              [styles.itemMuted]: isFilteredOut,
                            })}
                            endSlot={
                              <>
                                {!isFilteredOut && (
                                  <span className={styles.resultsText}>
                                    {`${numResults} Result${numResults === 1 ? '' : 's'}`}
                                  </span>
                                )}
                                {renderStatusIcon(numResults, isFilteredOut)}
                              </>
                            }
                          >
                            {`Repeat ${repeat.index + 1}`}
                          </Select.Item>
                        );
                      })}
                  </Select.Group>
                  {!isLastPage && <Select.Separator />}
                </Fragment>
              );
            })}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};

export default StepAndRepeatDropdown;
